/**
 * THE PLATFORM `fetch`, CALLABLE DETACHED -- the one spelling every DI seam defaults to.
 *
 * WHY IT EXISTS AT ALL: `fetch` is a method of the global scope, and workerd enforces that. A bare reference handed
 * to a collaborator and invoked as `call.fetchFn(...)` is invoked with the WRONG `this`, and the runtime refuses it
 * with `Illegal invocation: function called with incorrect \`this\` reference` -- at CALL time, from inside whichever
 * leaf took the default, never at the site that wrote `= fetch`. WITNESSED 2026-08-31: the
 * `aisearch-instance-provision` readback failed with exactly that message, relayed as an AI Search "network error",
 * which reads as an account or token fault rather than a binding defect one layer down.
 *
 * IT IS A WRAPPER, NOT A `.bind()`, and the difference is what keeps the seam honest: `globalThis.fetch.bind(globalThis)`
 * captures the global ONCE at module load, so a suite that installs a `globalThis.fetch` double afterwards is silently
 * not used. The arrow resolves `fetch` per call, so a test double installed at any point is picked up -- and the
 * production path pays one closure call.
 *
 * THREE SPELLINGS COLLAPSE HERE. Before this leaf the tree wrote one decision three ways -- the broken bare `= fetch`,
 * the correct `= (...a) => fetch(...a)`, and the load-time `globalThis.fetch.bind(globalThis)` -- where two of the
 * three happened to work, which is exactly why the broken one survived review. No count is spelled: the census rots,
 * and the invariant that does not is that a seam wanting a default takes THIS symbol and no other.
 *
 * HOMED IN `utils/http/` because its consumers span `tools/`, `components/` and the flow-engine composition roots,
 * and the utils floor is the layer all three reach. It value-imports nothing, so
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` is satisfied by construction.
 */
export const platformFetch: typeof fetch = (...args: Parameters<typeof fetch>) => fetch(...args);
